import { Injectable, OnDestroy, signal } from '@angular/core';
import { fromEvent } from 'rxjs';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ModifierKeysPressedService implements OnDestroy {
  private destroy$ = new Subject<void>();

  private ctrl = signal<boolean>(false);
  private shift = signal<boolean>(false);
  private alt = signal<boolean>(false);
  private meta = signal<boolean>(false);

  // Expose as readonly signals
  readonly isCtrlPressed = this.ctrl.asReadonly();
  readonly isShiftPressed = this.shift.asReadonly();
  readonly isAltPressed = this.alt.asReadonly();
  readonly isMetaPressed = this.meta.asReadonly();

  constructor() {
    // Use capture phase so stopPropagation() on inner elements (e.g. popover root)
    // cannot block modifier-key tracking at the document level.
    fromEvent<KeyboardEvent>(document, 'keydown', { capture: true })
      .pipe(takeUntil(this.destroy$))
      .subscribe((e) => this.updateModifiers(e));

    fromEvent<KeyboardEvent>(document, 'keyup', { capture: true })
      .pipe(takeUntil(this.destroy$))
      .subscribe((e) => this.updateModifiers(e));

    // Handle window blur to reset modifiers
    fromEvent(window, 'blur')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.reset());
  }

  private updateModifiers(event: KeyboardEvent): void {
    this.ctrl.set(event.ctrlKey);
    this.shift.set(event.shiftKey);
    this.alt.set(event.altKey);
    this.meta.set(event.metaKey);
  }

  private reset(): void {
    this.ctrl.set(false);
    this.shift.set(false);
    this.alt.set(false);
    this.meta.set(false);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
